import { Trash2 } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router";

import {
  DetailsRail,
  DetailsRailSection,
} from "@/shared/components/DetailsRail";
import {
  folderOf,
  UrdfPreviewPanel,
} from "@/shared/components/devices/UrdfPreviewPanel";
import { FactList } from "@/shared/components/FactList";
import { MetadataChangesetEditor } from "@/shared/components/Metadata";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { FormattedDateTime } from "@/shared/datetime";
import { useUpdateDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type MetadataChangeset } from "@/shared/domain/metadataChangeset";
import { RobotoButton } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { useDeviceTypeContext } from "../DeviceType/useDeviceTypeContext";

import { DeleteDeviceTypeDialog } from "./DeleteDeviceTypeDialog";
import styles from "./DeviceTypeDetails.module.css";

/**
 * Details tab of a device type. Laid out like a device's: a Properties card
 * of the type's ID, inline editors for its tags and metadata, and who created
 * and last changed it; then an Operations card with Delete. A column to the
 * right previews the type's URDF. The name and description are edited in the
 * page's header.
 */
export function DeviceTypeDetails() {
  const { deviceType } = useDeviceTypeContext();
  const navigate = useNavigate();
  // One mutation per editor, so a failed tag edit shows its error under Tags
  // only and leaves Metadata usable.
  const tagsUpdate = useUpdateDeviceType();
  const metadataUpdate = useUpdateDeviceType();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  // An org without this flag cannot ingest a URDF, so there is nothing to
  // preview.
  const previewsEnabled = useCheckFeatureFlag(
    "release-3d-assets",
    deviceType.org_id,
  );

  const changesetRequest = (changeset: MetadataChangeset) => ({
    deviceTypeId: deviceType.device_type_id,
    resourceOwnerId: deviceType.org_id,
    updates: { metadata_changeset: changeset },
  });

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <section className={styles.card}>
          <h3 className={styles.cardHeading}>Properties</h3>
          <FactList divided className={styles.properties}>
            <FactList.Item label="Device Type ID" mono>
              {deviceType.device_type_id}
            </FactList.Item>

            <FactList.Item label="Tags">
              <TagsEditor
                tags={deviceType.tags}
                recordKey={deviceType.device_type_id}
                autocompleteType={AutocompleteType.GenericTags}
                onAdd={(tag) =>
                  tagsUpdate.mutateAsync(changesetRequest({ put_tags: [tag] }))
                }
                onRemove={(tag) =>
                  tagsUpdate.mutateAsync(
                    changesetRequest({ remove_tags: [tag] }),
                  )
                }
              />
            </FactList.Item>

            <FactList.Item label="Metadata">
              <MetadataChangesetEditor
                metadata={deviceType.metadata}
                recordKey={deviceType.device_type_id}
                onChangeset={(changeset) =>
                  metadataUpdate.mutate(changesetRequest(changeset))
                }
                isPending={metadataUpdate.isPending}
                error={metadataUpdate.error}
                onDismissError={() => metadataUpdate.reset()}
              />
            </FactList.Item>

            <FactList.Item label="Created">
              <FormattedDateTime value={deviceType.created} />
              {" by "}
              <RobotoPrincipalUnderlineLink
                className={styles.inlinePrincipal}
                principal={deviceType.created_by}
              />
            </FactList.Item>

            <FactList.Item label="Updated">
              <FormattedDateTime value={deviceType.modified} />
              {" by "}
              <RobotoPrincipalUnderlineLink
                className={styles.inlinePrincipal}
                principal={deviceType.modified_by}
              />
            </FactList.Item>
          </FactList>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardHeading}>Operations</h3>
          <div className={styles.operations}>
            <RobotoButton
              variant="destructive-secondary"
              icon={<Trash2 aria-hidden="true" />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete device type
            </RobotoButton>
          </div>
        </section>
      </div>

      {previewsEnabled && (
        <DetailsRail>
          <DetailsRailSection label="URDF">
            <UrdfPreviewPanel
              associationId={deviceType.device_type_id}
              orgId={deviceType.org_id}
              owner="device type"
              folderHref={(urdf) =>
                Goto.createAssociationRoute(
                  deviceType.device_type_id,
                  folderOf(urdf.relative_path),
                )
              }
            />
          </DetailsRailSection>
        </DetailsRail>
      )}

      <DeleteDeviceTypeDialog
        deviceType={deviceType}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => void navigate(Goto.route.DeviceTypes)}
      />
    </div>
  );
}
